import React, { useState, useEffect } from 'react';
import { AlertTriangle, Loader2, Save, Trash2, X, Settings } from 'lucide-react';
import { itemsService } from '../../../../../../services/itemsService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../../../services/stcBinGrpService';
import { stcMovementTypeService, StcMovementTypeDto } from '../../../../../../services/stcMovementTypeService';
import { stcSortingBoxDefService, StcSortingBoxDefDto } from '../../../../../../services/stcSortingBoxDefService';
import reportConfigurationService, { ReportConfiguration } from '../../../../../../services/reportConfigurationService';
import { itemBinSizeGroupService } from '../../../../../../services/itemBinSizeGroupService';
import { ItemBinSizeGroupDto } from '../../../../../../types/itemBinSizeGroup';
import { ItemsDto, BulkUpdateItemsRequest, BulkDeleteItemsRequest, BulkItemOperationResponse } from '../../../../../../types/items';
import { useTranslation } from '../../../../../../contexts/I18nProvider';

interface ItemsBulkUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedItemNos: string[];
    companyNo: string;
    onUpdateSuccess: () => void;
    onDeleteSuccess: () => void;
    /** Optional: item data for pre-filling when single record is selected */
    selectedItemData?: ItemsDto[];
}

interface UpdateFields {
    description1: boolean;
    itemBarCode: boolean;
    supplierItemNo: boolean;
    binGrpNo: boolean;
    pickRuleNo: boolean;
    reportID: boolean;
    movementTypeNo: boolean;
    stcBoxTypeID: boolean;
    itemBinSizeGroupID: boolean;
    stcTransactionsYesNo: boolean;
    putAwayToPickableBinYesNo: boolean;
    newLineYesNo: boolean;
    barCodeConfirmItem: boolean;
    itemGrp1: boolean;
    itemGrp2: boolean;
    itemGrp3: boolean;
    stdLocationName: boolean;
    stdBinCode: boolean;
    useSharedBinYesNo: boolean;
    useMaxReplenishQtyYesNo: boolean;
    maxReplenishQty: boolean;
    freeTxt1: boolean;
    freeTxt2: boolean;
    freeTxt3: boolean;
    freeTxt4: boolean;
    freeTxt5: boolean;
    freeTxt6: boolean;
    freeTxt7: boolean;
    freeTxt8: boolean;
    freeTxt9: boolean;
    freeTxt10: boolean;
}

type TabType = 'basics' | 'warehouse' | 'classification' | 'location' | 'freetext';

type TFunc = (key: string, params?: Record<string, string | number | null | undefined>, defaultText?: string) => string;

const getPickRuleOptions = (t: TFunc) => [
    { value: 1, label: t('manager.modules.adminItemsLocations.itemsBulk.pickRule1', {}, '1 - FIFO (First In First Out)') },
    { value: 2, label: t('manager.modules.adminItemsLocations.itemsBulk.pickRule2', {}, '2 - Antall') },
    { value: 3, label: t('manager.modules.adminItemsLocations.itemsBulk.pickRule3', {}, '3 - Prioritet') },
    { value: 4, label: t('manager.modules.adminItemsLocations.itemsBulk.pickRule4', {}, '4 - Batch fra ERP') }
];

/**
 * Bulk update modal for items with tabbed interface (5 tabs)
 * Adapted from BulkUpdateModal for location management
 */
export const ItemsBulkUpdateModal: React.FC<ItemsBulkUpdateModalProps> = ({
    isOpen,
    onClose,
    selectedItemNos,
    companyNo,
    onUpdateSuccess,
    onDeleteSuccess,
    selectedItemData
}) => {
    const { t } = useTranslation();
    const PICK_RULE_OPTIONS = getPickRuleOptions(t);

    // Detect single selection for pre-filling
    const isSingleSelection = selectedItemNos.length === 1;
    const singleItem = isSingleSelection && selectedItemData?.length === 1 ? selectedItemData[0] : null;

    const [activeTab, setActiveTab] = useState<TabType>('basics');

    // Master data from API
    const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);
    const [reports, setReports] = useState<ReportConfiguration[]>([]);
    const [movementTypes, setMovementTypes] = useState<StcMovementTypeDto[]>([]);
    const [sortingBoxTypes, setSortingBoxTypes] = useState<StcSortingBoxDefDto[]>([]);
    const [isLoadingBinGroups, setIsLoadingBinGroups] = useState(false);
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [isLoadingMovementTypes, setIsLoadingMovementTypes] = useState(false);
    const [isLoadingSortingBoxTypes, setIsLoadingSortingBoxTypes] = useState(false);
    const [itemBinSizeGroups, setItemBinSizeGroups] = useState<ItemBinSizeGroupDto[]>([]);
    const [isLoadingItemBinSizeGroups, setIsLoadingItemBinSizeGroups] = useState(false);

    // Track which fields should be updated (enable all when single selection for pre-fill)
    const [updateFields, setUpdateFields] = useState<UpdateFields>({
        description1: isSingleSelection,
        itemBarCode: isSingleSelection,
        supplierItemNo: isSingleSelection,
        binGrpNo: isSingleSelection,
        pickRuleNo: isSingleSelection,
        reportID: isSingleSelection,
        movementTypeNo: isSingleSelection,
        stcBoxTypeID: isSingleSelection,
        itemBinSizeGroupID: isSingleSelection,
        stcTransactionsYesNo: isSingleSelection,
        putAwayToPickableBinYesNo: isSingleSelection,
        newLineYesNo: isSingleSelection,
        barCodeConfirmItem: isSingleSelection,
        itemGrp1: isSingleSelection,
        itemGrp2: isSingleSelection,
        itemGrp3: isSingleSelection,
        stdLocationName: isSingleSelection,
        stdBinCode: isSingleSelection,
        useSharedBinYesNo: isSingleSelection,
        useMaxReplenishQtyYesNo: isSingleSelection,
        maxReplenishQty: isSingleSelection,
        freeTxt1: isSingleSelection,
        freeTxt2: isSingleSelection,
        freeTxt3: isSingleSelection,
        freeTxt4: isSingleSelection,
        freeTxt5: isSingleSelection,
        freeTxt6: isSingleSelection,
        freeTxt7: isSingleSelection,
        freeTxt8: isSingleSelection,
        freeTxt9: isSingleSelection,
        freeTxt10: isSingleSelection
    });

    // Field values - Tab 1: Grunnleggende (pre-fill from singleItem when single selection)
    const [description1, setDescription1] = useState<string>(singleItem?.description1 ?? '');
    const [itemBarCode, setItemBarCode] = useState<string>(singleItem?.itemBarCode ?? '');
    const [supplierItemNo, setSupplierItemNo] = useState<string>(singleItem?.supplierItemNo ?? '');
    const [binGrpNo, setBinGrpNo] = useState<number | undefined>(singleItem?.binGrpNo ?? undefined);
    const [pickRuleNo, setPickRuleNo] = useState<number>(singleItem?.pickRuleNo ?? 1);
    const [reportID, setReportID] = useState<number | undefined>(singleItem?.reportID ?? undefined);

    // Field values - Tab 2: Lagerkonfigurasjon
    const [movementTypeNo, setMovementTypeNo] = useState<number | undefined>(singleItem?.movementTypeNo ?? undefined);
    const [stcBoxTypeID, setStcBoxTypeID] = useState<string>(singleItem?.stcBoxTypeID ?? '');
    const [itemBinSizeGroupID, setItemBinSizeGroupID] = useState<number | undefined>(singleItem?.itemBinSizeGroupID ?? undefined);
    const [stcTransactionsYesNo, setStcTransactionsYesNo] = useState<boolean>(singleItem?.stcTransactionsYesNo ?? true);
    const [putAwayToPickableBinYesNo, setPutAwayToPickableBinYesNo] = useState<boolean>(singleItem?.putAwayToPickableBinYesNo ?? false);
    const [newLineYesNo, setNewLineYesNo] = useState<boolean>(singleItem?.newLineYesNo ?? false);
    // Tri-state (TW 36344429): '' = arv fra profil, 'true'/'false' = overstyr
    const [barCodeConfirmItem, setBarCodeConfirmItem] = useState<string>(
        singleItem?.barCodeConfirmItem === true ? 'true'
            : singleItem?.barCodeConfirmItem === false ? 'false'
            : ''
    );

    // Field values - Tab 3: Klassifisering
    const [itemGrp1, setItemGrp1] = useState<string>(singleItem?.itemGrp1 ?? '');
    const [itemGrp2, setItemGrp2] = useState<string>(singleItem?.itemGrp2 ?? '');
    const [itemGrp3, setItemGrp3] = useState<string>(singleItem?.itemGrp3 ?? '');

    // Field values - Tab 4: Lokasjon
    const [stdLocationName, setStdLocationName] = useState<string>(singleItem?.stdLocationName ?? '');
    const [stdBinCode, setStdBinCode] = useState<string>(singleItem?.stdBinCode ?? '');
    const [useSharedBinYesNo, setUseSharedBinYesNo] = useState<boolean>(singleItem?.useSharedBinYesNo ?? false);
    const [useMaxReplenishQtyYesNo, setUseMaxReplenishQtyYesNo] = useState<boolean>(singleItem?.useMaxReplenishQtyYesNo ?? false);
    const [maxReplenishQty, setMaxReplenishQty] = useState<number>(singleItem?.maxReplenishQty ?? 0);

    // Field values - Tab 5: Fritekst
    const [freeTxt1, setFreeTxt1] = useState<string>(singleItem?.freeTxt1 ?? '');
    const [freeTxt2, setFreeTxt2] = useState<string>(singleItem?.freeTxt2 ?? '');
    const [freeTxt3, setFreeTxt3] = useState<string>(singleItem?.freeTxt3 ?? '');
    const [freeTxt4, setFreeTxt4] = useState<string>(singleItem?.freeTxt4 ?? '');
    const [freeTxt5, setFreeTxt5] = useState<string>(singleItem?.freeTxt5 ?? '');
    const [freeTxt6, setFreeTxt6] = useState<string>(singleItem?.freeTxt6 ?? '');
    const [freeTxt7, setFreeTxt7] = useState<string>(singleItem?.freeTxt7 ?? '');
    const [freeTxt8, setFreeTxt8] = useState<string>(singleItem?.freeTxt8 ?? '');
    const [freeTxt9, setFreeTxt9] = useState<string>(singleItem?.freeTxt9 ?? '');
    const [freeTxt10, setFreeTxt10] = useState<string>(singleItem?.freeTxt10 ?? '');

    // State
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [notification, setNotification] = useState<{
        type: 'success' | 'error' | 'warning';
        message: string;
    } | null>(null);

    // Load master data on mount
    useEffect(() => {
        if (!isOpen || !companyNo) return;

        const loadData = async () => {
            // Load bin groups
            setIsLoadingBinGroups(true);
            try {
                const groups = await stcBinGrpService.getCurrent();
                setBinGroups(groups);
                if (groups.length > 0 && !singleItem) {
                    setBinGrpNo(groups[0].binGrpNo);
                }
            } catch (err) {
                console.error('Failed to load bin groups:', err);
            } finally {
                setIsLoadingBinGroups(false);
            }

            // Load reports
            setIsLoadingReports(true);
            try {
                const reportList = await reportConfigurationService.getAll(companyNo);
                setReports(reportList);
                if (reportList.length > 0 && !singleItem) {
                    setReportID(reportList[0].reportID);
                }
            } catch (err) {
                console.error('Failed to load reports:', err);
            } finally {
                setIsLoadingReports(false);
            }

            // Load movement types
            setIsLoadingMovementTypes(true);
            try {
                const types = await stcMovementTypeService.getCurrent();
                setMovementTypes(types);
                if (types.length > 0 && !singleItem) {
                    setMovementTypeNo(types[0].movementTypeNo);
                }
            } catch (err) {
                console.error('Failed to load movement types:', err);
            } finally {
                setIsLoadingMovementTypes(false);
            }

            // Load sorting box types
            setIsLoadingSortingBoxTypes(true);
            try {
                const boxTypes = await stcSortingBoxDefService.getCurrent();
                setSortingBoxTypes(boxTypes);
                if (boxTypes.length > 0 && !singleItem) {
                    setStcBoxTypeID(boxTypes[0].stcBoxTypeID);
                }
            } catch (err) {
                console.error('Failed to load sorting box types:', err);
            } finally {
                setIsLoadingSortingBoxTypes(false);
            }

            // Load item bin size groups
            setIsLoadingItemBinSizeGroups(true);
            try {
                const groups = await itemBinSizeGroupService.getCurrent().catch(() => []);
                setItemBinSizeGroups(groups);
            } catch (err) {
                console.error('Failed to load item bin size groups:', err);
            } finally {
                setIsLoadingItemBinSizeGroups(false);
            }
        };

        loadData();
    }, [isOpen, companyNo]);

    // Reset fields when selection changes (skip reset for single selection with pre-filled data)
    useEffect(() => {
        if (!isOpen) return;
        if (isSingleSelection && singleItem) return; // Don't reset pre-filled values

        setUpdateFields({
            description1: false,
            itemBarCode: false,
            supplierItemNo: false,
            binGrpNo: false,
            pickRuleNo: false,
            reportID: false,
            movementTypeNo: false,
            stcBoxTypeID: false,
            itemBinSizeGroupID: false,
            stcTransactionsYesNo: false,
            putAwayToPickableBinYesNo: false,
            newLineYesNo: false,
            barCodeConfirmItem: false,
            itemGrp1: false,
            itemGrp2: false,
            itemGrp3: false,
            stdLocationName: false,
            stdBinCode: false,
            useSharedBinYesNo: false,
            useMaxReplenishQtyYesNo: false,
            maxReplenishQty: false,
            freeTxt1: false,
            freeTxt2: false,
            freeTxt3: false,
            freeTxt4: false,
            freeTxt5: false,
            freeTxt6: false,
            freeTxt7: false,
            freeTxt8: false,
            freeTxt9: false,
            freeTxt10: false
        });
        setNotification(null);
        setActiveTab('basics');
    }, [selectedItemNos, isOpen, isSingleSelection, singleItem]);

    // Auto-hide notification after 5 seconds
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // Check if at least one field is selected for update
    const hasSelectedFields = (): boolean => {
        return Object.values(updateFields).some(v => v);
    };

    // Build update request with only selected fields
    const buildUpdateRequest = (): BulkUpdateItemsRequest => {
        const request: BulkUpdateItemsRequest = {
            companyNo,
            itemNos: selectedItemNos
        };

        if (updateFields.description1) request.description1 = description1;
        if (updateFields.itemBarCode) request.itemBarCode = itemBarCode;
        if (updateFields.supplierItemNo) request.supplierItemNo = supplierItemNo;
        if (updateFields.binGrpNo) request.binGrpNo = binGrpNo;
        if (updateFields.pickRuleNo) request.pickRuleNo = pickRuleNo;
        if (updateFields.reportID) request.reportID = reportID;
        if (updateFields.movementTypeNo) request.movementTypeNo = movementTypeNo;
        if (updateFields.stcBoxTypeID) request.stcBoxTypeID = stcBoxTypeID;
        if (updateFields.itemBinSizeGroupID) request.itemBinSizeGroupID = itemBinSizeGroupID;
        if (updateFields.stcTransactionsYesNo) request.stcTransactionsYesNo = stcTransactionsYesNo;
        if (updateFields.putAwayToPickableBinYesNo) request.putAwayToPickableBinYesNo = putAwayToPickableBinYesNo;
        if (updateFields.newLineYesNo) request.newLineYesNo = newLineYesNo;
        // Tri-state: tomt valg = sett NULL (arv fra profil) — backend tolker 'inherit' som NULL
        if (updateFields.barCodeConfirmItem) request.barCodeConfirmItem = barCodeConfirmItem === '' ? 'inherit' : barCodeConfirmItem;
        if (updateFields.itemGrp1) request.itemGrp1 = itemGrp1;
        if (updateFields.itemGrp2) request.itemGrp2 = itemGrp2;
        if (updateFields.itemGrp3) request.itemGrp3 = itemGrp3;
        if (updateFields.stdLocationName) request.stdLocationName = stdLocationName;
        if (updateFields.stdBinCode) request.stdBinCode = stdBinCode;
        if (updateFields.useSharedBinYesNo) request.useSharedBinYesNo = useSharedBinYesNo;
        if (updateFields.useMaxReplenishQtyYesNo) request.useMaxReplenishQtyYesNo = useMaxReplenishQtyYesNo;
        if (updateFields.maxReplenishQty) request.maxReplenishQty = maxReplenishQty;
        if (updateFields.freeTxt1) request.freeTxt1 = freeTxt1;
        if (updateFields.freeTxt2) request.freeTxt2 = freeTxt2;
        if (updateFields.freeTxt3) request.freeTxt3 = freeTxt3;
        if (updateFields.freeTxt4) request.freeTxt4 = freeTxt4;
        if (updateFields.freeTxt5) request.freeTxt5 = freeTxt5;
        if (updateFields.freeTxt6) request.freeTxt6 = freeTxt6;
        if (updateFields.freeTxt7) request.freeTxt7 = freeTxt7;
        if (updateFields.freeTxt8) request.freeTxt8 = freeTxt8;
        if (updateFields.freeTxt9) request.freeTxt9 = freeTxt9;
        if (updateFields.freeTxt10) request.freeTxt10 = freeTxt10;

        return request;
    };

    // Get descriptions for display
    const getBinGrpDescription = (grpNo: number): string => {
        const group = binGroups.find(g => g.binGrpNo === grpNo);
        return group ? `${grpNo} - ${group.description1}` : `${grpNo}`;
    };

    const getReportDescription = (repID: number): string => {
        const report = reports.find(r => r.reportID === repID);
        return report ? `${repID} - ${report.reportName}` : `${repID}`;
    };

    const getMovementTypeDescription = (typeNo: number): string => {
        const type = movementTypes.find(t => t.movementTypeNo === typeNo);
        return type ? `${typeNo} - ${type.description1}` : `${typeNo}`;
    };

    const getSortingBoxTypeDescription = (boxTypeID: string): string => {
        const boxType = sortingBoxTypes.find(b => b.stcBoxTypeID === boxTypeID);
        return boxType ? `${boxTypeID} - ${boxType.description1 || t('manager.modules.adminItemsLocations.itemsBulk.sortingBox', {}, 'Sorting Box')}` : boxTypeID;
    };

    const getPickRuleDescription = (ruleNo: number): string => {
        const rule = PICK_RULE_OPTIONS.find(r => r.value === ruleNo);
        return rule ? rule.label : `${ruleNo}`;
    };

    // Get summary of fields that will be updated
    const getUpdateSummary = (): string[] => {
        const summary: string[] = [];

        const yes = t('manager.modules.adminItemsLocations.itemsBulk.yes', {}, 'Ja');
        const no = t('manager.modules.adminItemsLocations.itemsBulk.no', {}, 'Nei');
        const none = t('manager.modules.adminItemsLocations.itemsBulk.none', {}, 'Ingen');

        if (updateFields.description1) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldDescription', {}, 'Beskrivelse')}: ${description1}`);
        if (updateFields.itemBarCode) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldGtin', {}, 'GTIN')}: ${itemBarCode}`);
        if (updateFields.supplierItemNo) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldSupplierItem', {}, 'Leverandorvare')}: ${supplierItemNo}`);
        if (updateFields.binGrpNo) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldBinGrp', {}, 'Lokasjonsgruppe')}: ${binGrpNo ? getBinGrpDescription(binGrpNo) : none}`);
        if (updateFields.pickRuleNo) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldPickRule', {}, 'Plukkregel')}: ${getPickRuleDescription(pickRuleNo)}`);
        if (updateFields.reportID) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldReport', {}, 'Rapport')}: ${reportID ? getReportDescription(reportID) : none}`);
        if (updateFields.movementTypeNo) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldMovementType', {}, 'Bevegelseskode')}: ${movementTypeNo ? getMovementTypeDescription(movementTypeNo) : none}`);
        if (updateFields.stcBoxTypeID) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldSortingBox', {}, 'Sorteringskasse')}: ${stcBoxTypeID ? getSortingBoxTypeDescription(stcBoxTypeID) : none}`);
        if (updateFields.itemBinSizeGroupID) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldSizeGroup', {}, 'Størrelsegruppe')}: ${itemBinSizeGroupID ? itemBinSizeGroups.find(g => g.itemBinSizeGroupID === itemBinSizeGroupID)?.description1 || String(itemBinSizeGroupID) : none}`);
        if (updateFields.stcTransactionsYesNo) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldStockControl', {}, 'Lagerstyring')}: ${stcTransactionsYesNo ? yes : no}`);
        if (updateFields.putAwayToPickableBinYesNo) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldReceiveToPick', {}, 'Mottak til plukk')}: ${putAwayToPickableBinYesNo ? yes : no}`);
        if (updateFields.newLineYesNo) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldNewLine', {}, 'Ny linje')}: ${newLineYesNo ? yes : no}`);
        if (updateFields.barCodeConfirmItem) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldBarCodeConfirmItem', {}, 'Krev vareskann')}: ${barCodeConfirmItem === 'true' ? yes : barCodeConfirmItem === 'false' ? no : t('manager.modules.adminItemsLocations.itemsBulk.scanFlagInherit', {}, 'Arv (fra profil)')}`);
        if (updateFields.itemGrp1) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldItemGrp1', {}, 'Varegruppe 1')}: ${itemGrp1}`);
        if (updateFields.itemGrp2) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldItemGrp2', {}, 'Varegruppe 2')}: ${itemGrp2}`);
        if (updateFields.itemGrp3) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldItemGrp3', {}, 'Varegruppe 3')}: ${itemGrp3}`);
        if (updateFields.stdLocationName) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldStdLocation', {}, 'Standard lokasjon')}: ${stdLocationName}`);
        if (updateFields.stdBinCode) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldStdBinCode', {}, 'Fast plukklokasjon')}: ${stdBinCode}`);
        if (updateFields.useSharedBinYesNo) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldSharedBin', {}, 'Delt lokasjon')}: ${useSharedBinYesNo ? yes : no}`);
        if (updateFields.useMaxReplenishQtyYesNo) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldUseMaxReplenish', {}, 'Bruk maks påfyllmengde')}: ${useMaxReplenishQtyYesNo ? yes : no}`);
        if (updateFields.maxReplenishQty) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.fieldMaxReplenish', {}, 'Maks påfyllmengde')}: ${maxReplenishQty}`);
        if (updateFields.freeTxt1) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 1 }, 'Fritekst 1')}: ${freeTxt1}`);
        if (updateFields.freeTxt2) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 2 }, 'Fritekst 2')}: ${freeTxt2}`);
        if (updateFields.freeTxt3) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 3 }, 'Fritekst 3')}: ${freeTxt3}`);
        if (updateFields.freeTxt4) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 4 }, 'Fritekst 4')}: ${freeTxt4}`);
        if (updateFields.freeTxt5) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 5 }, 'Fritekst 5')}: ${freeTxt5}`);
        if (updateFields.freeTxt6) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 6 }, 'Fritekst 6')}: ${freeTxt6}`);
        if (updateFields.freeTxt7) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 7 }, 'Fritekst 7')}: ${freeTxt7}`);
        if (updateFields.freeTxt8) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 8 }, 'Fritekst 8')}: ${freeTxt8}`);
        if (updateFields.freeTxt9) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 9 }, 'Fritekst 9')}: ${freeTxt9}`);
        if (updateFields.freeTxt10) summary.push(`${t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': 10 }, 'Fritekst 10')}: ${freeTxt10}`);

        return summary;
    };

    // Handle update operation
    const handleUpdate = async () => {
        if (!hasSelectedFields()) {
            setNotification({
                type: 'error',
                message: t('manager.modules.adminItemsLocations.itemsBulk.errorNoFields', {}, 'Du ma velge minst ett felt som skal oppdateres')
            });
            return;
        }

        setShowUpdateConfirm(true);
    };

    // Confirm and execute update
    const executeUpdate = async () => {
        setIsUpdating(true);
        setShowUpdateConfirm(false);
        setNotification(null);

        try {
            const request = buildUpdateRequest();
            const response: BulkItemOperationResponse = await itemsService.bulkUpdateItems(request);

            if (response.errors.length > 0) {
                setNotification({
                    type: 'warning',
                    message: t('manager.modules.adminItemsLocations.itemsBulk.updatePartial', { '0': response.successCount, '1': selectedItemNos.length, '2': response.errors.length }, `Oppdatert ${response.successCount} av ${selectedItemNos.length} varer. ${response.errors.length} feil oppstod.`)
                });
            } else {
                setNotification({
                    type: 'success',
                    message: t('manager.modules.adminItemsLocations.itemsBulk.updateSuccess', { '0': response.successCount }, `${response.successCount} vare(r) oppdatert`)
                });
            }

            // Refresh and clear selection
            onUpdateSuccess();
        } catch (err) {
            setNotification({
                type: 'error',
                message: err instanceof Error ? err.message : t('manager.modules.adminItemsLocations.itemsBulk.updateFailed', {}, 'Oppdatering feilet')
            });
        } finally {
            setIsUpdating(false);
        }
    };

    // Handle delete operation
    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    // Confirm and execute delete
    const executeDelete = async () => {
        setIsDeleting(true);
        setShowDeleteConfirm(false);
        setNotification(null);

        try {
            const request: BulkDeleteItemsRequest = {
                companyNo,
                itemNos: selectedItemNos,
                forceDelete: false
            };

            const response: BulkItemOperationResponse = await itemsService.bulkDeleteItems(request);

            if (response.errors.length > 0) {
                setNotification({
                    type: 'warning',
                    message: t('manager.modules.adminItemsLocations.itemsBulk.deletePartial', { '0': response.successCount, '1': selectedItemNos.length, '2': response.errors.length }, `Slettet ${response.successCount} av ${selectedItemNos.length} varer. ${response.errors.length} kunne ikke slettes.`)
                });
            } else {
                setNotification({
                    type: 'success',
                    message: t('manager.modules.adminItemsLocations.itemsBulk.deleteSuccess', { '0': response.successCount }, `${response.successCount} vare(r) slettet`)
                });
            }

            // Refresh and clear selection
            onDeleteSuccess();
        } catch (err) {
            setNotification({
                type: 'error',
                message: err instanceof Error ? err.message : t('manager.modules.adminItemsLocations.itemsBulk.deleteFailed', {}, 'Sletting feilet')
            });
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen) return null;

    const tabs: { id: TabType; label: string }[] = [
        { id: 'basics', label: t('manager.modules.adminItemsLocations.itemsBulk.tabBasics', {}, 'Grunnleggende') },
        { id: 'warehouse', label: t('manager.modules.adminItemsLocations.itemsBulk.tabWarehouse', {}, 'Lagerkonfigurasjon') },
        { id: 'classification', label: t('manager.modules.adminItemsLocations.itemsBulk.tabClassification', {}, 'Klassifisering') },
        { id: 'location', label: t('manager.modules.adminItemsLocations.itemsBulk.tabLocation', {}, 'Lokasjon') },
        { id: 'freetext', label: t('manager.modules.adminItemsLocations.itemsBulk.tabFreeText', {}, 'Fritekst') }
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <Settings className="w-6 h-6 text-blue-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{t('manager.modules.adminItemsLocations.itemsBulk.title', {}, 'Masseoppdatering av varer')}</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {t('manager.modules.adminItemsLocations.itemsBulk.itemsSelected', { '0': selectedItemNos.length }, `${selectedItemNos.length} vare(r) valgt`)}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Notification */}
                {notification && (
                    <div className={`mx-6 mt-4 rounded-lg p-3 flex items-start space-x-2 ${
                        notification.type === 'success' ? 'bg-green-50 border border-green-200' :
                        notification.type === 'error' ? 'bg-red-50 border border-red-200' :
                        'bg-yellow-50 border border-yellow-200'
                    }`}>
                        {notification.type === 'success' && <Settings className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'error' && <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />}
                        <p className={`text-sm flex-1 ${
                            notification.type === 'success' ? 'text-green-800' :
                            notification.type === 'error' ? 'text-red-800' :
                            'text-yellow-800'
                        }`}>
                            {notification.message}
                        </p>
                        <button
                            onClick={() => setNotification(null)}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="border-b border-gray-200 px-6">
                    <div className="flex space-x-8 overflow-x-auto">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                                    activeTab === tab.id
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-4">
                        {/* Tab 1: Grunnleggende (Basics) */}
                        {activeTab === 'basics' && (
                            <>
                                {/* Description1 */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.description1}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, description1: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('common.fields.description', {}, 'Beskrivelse')}</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={description1}
                                        onChange={(e) => setDescription1(e.target.value)}
                                        disabled={!updateFields.description1}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        placeholder={t('manager.modules.adminItemsLocations.itemsBulk.itemDescriptionPlaceholder', {}, 'Varebeskrivelse')}
                                    />
                                </div>

                                {/* ItemBarCode */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.itemBarCode}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, itemBarCode: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.gtinLabel', {}, 'GTIN / Strekkode')}</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={itemBarCode}
                                        onChange={(e) => setItemBarCode(e.target.value)}
                                        disabled={!updateFields.itemBarCode}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        placeholder={t('manager.modules.adminItemsLocations.itemsBulk.gtinLabel', {}, 'GTIN / Strekkode')}
                                    />
                                </div>

                                {/* SupplierItemNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.supplierItemNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, supplierItemNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.supplierItemNoLabel', {}, 'Leverandorvarenummer')}</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={supplierItemNo}
                                        onChange={(e) => setSupplierItemNo(e.target.value)}
                                        disabled={!updateFields.supplierItemNo}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        placeholder={t('manager.modules.adminItemsLocations.itemsBulk.supplierItemNoPlaceholder', {}, 'Leverandorens varenummer')}
                                    />
                                </div>

                                {/* BinGrpNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.binGrpNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, binGrpNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldBinGrp', {}, 'Lokasjonsgruppe')}</span>
                                    </label>
                                    <select
                                        value={binGrpNo || ''}
                                        onChange={(e) => setBinGrpNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.binGrpNo || isLoadingBinGroups}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.adminItemsLocations.itemsBulk.none', {}, 'Ingen')}</option>
                                        {isLoadingBinGroups ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : binGroups.length > 0 ? (
                                            binGroups.map(group => (
                                                <option key={group.binGrpNo} value={group.binGrpNo}>
                                                    {group.binGrpNo} - {group.description1}
                                                </option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* PickRuleNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.pickRuleNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, pickRuleNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldPickRule', {}, 'Plukkregel')}</span>
                                    </label>
                                    <select
                                        value={pickRuleNo}
                                        onChange={(e) => setPickRuleNo(parseInt(e.target.value))}
                                        disabled={!updateFields.pickRuleNo}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        {PICK_RULE_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* ReportID */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.reportID}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, reportID: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.defaultLabelField', {}, 'Standard etikett')}</span>
                                    </label>
                                    <select
                                        value={reportID || ''}
                                        onChange={(e) => setReportID(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.reportID || isLoadingReports}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.adminItemsLocations.itemsBulk.none', {}, 'Ingen')}</option>
                                        {isLoadingReports ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : reports.length > 0 ? (
                                            reports.map(report => (
                                                <option key={report.reportID} value={report.reportID}>
                                                    {report.reportID} - {report.reportName}
                                                </option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>
                            </>
                        )}

                        {/* Tab 2: Lagerkonfigurasjon (Warehouse Config) */}
                        {activeTab === 'warehouse' && (
                            <>
                                {/* MovementTypeNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.movementTypeNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, movementTypeNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldMovementType', {}, 'Bevegelseskode')}</span>
                                    </label>
                                    <select
                                        value={movementTypeNo || ''}
                                        onChange={(e) => setMovementTypeNo(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.movementTypeNo || isLoadingMovementTypes}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.adminItemsLocations.itemsBulk.none', {}, 'Ingen')}</option>
                                        {isLoadingMovementTypes ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : movementTypes.length > 0 ? (
                                            movementTypes.map(type => (
                                                <option key={type.movementTypeNo} value={type.movementTypeNo}>
                                                    {type.movementTypeNo} - {type.description1}
                                                </option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* StcBoxTypeID */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.stcBoxTypeID}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, stcBoxTypeID: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldSortingBox', {}, 'Sorteringskasse')}</span>
                                    </label>
                                    <select
                                        value={stcBoxTypeID}
                                        onChange={(e) => setStcBoxTypeID(e.target.value)}
                                        disabled={!updateFields.stcBoxTypeID || isLoadingSortingBoxTypes}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.adminItemsLocations.itemsBulk.none', {}, 'Ingen')}</option>
                                        {isLoadingSortingBoxTypes ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : sortingBoxTypes.length > 0 ? (
                                            sortingBoxTypes.map(boxType => (
                                                <option key={boxType.stcBoxTypeID} value={boxType.stcBoxTypeID}>
                                                    {boxType.stcBoxTypeID} - {boxType.description1 || t('manager.modules.adminItemsLocations.itemsBulk.sortingBox', {}, 'Sorting Box')}
                                                </option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* ItemBinSizeGroupID */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.itemBinSizeGroupID}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, itemBinSizeGroupID: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldSizeGroup', {}, 'Størrelsegruppe')}</span>
                                    </label>
                                    <select
                                        value={itemBinSizeGroupID ?? ''}
                                        onChange={(e) => setItemBinSizeGroupID(e.target.value ? parseInt(e.target.value) : undefined)}
                                        disabled={!updateFields.itemBinSizeGroupID || isLoadingItemBinSizeGroups}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.adminItemsLocations.itemsBulk.none', {}, 'Ingen')}</option>
                                        {isLoadingItemBinSizeGroups ? (
                                            <option>{t('common.status.loading', {}, 'Laster...')}</option>
                                        ) : itemBinSizeGroups.length > 0 ? (
                                            itemBinSizeGroups.map(grp => (
                                                <option key={grp.itemBinSizeGroupID} value={grp.itemBinSizeGroupID}>
                                                    {grp.itemBinSizeGroupID} - {grp.description1}
                                                </option>
                                            ))
                                        ) : null}
                                    </select>
                                </div>

                                {/* StcTransactionsYesNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.stcTransactionsYesNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, stcTransactionsYesNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldStockControl', {}, 'Lagerstyring')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={stcTransactionsYesNo}
                                            onChange={(e) => setStcTransactionsYesNo(e.target.checked)}
                                            disabled={!updateFields.stcTransactionsYesNo}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.enableStockControl', {}, 'Aktiver lagerstyring')}</span>
                                    </label>
                                </div>

                                {/* PutAwayToPickableBinYesNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.putAwayToPickableBinYesNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, putAwayToPickableBinYesNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.receiveDirectToPick', {}, 'Mottak direkte til plukk')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={putAwayToPickableBinYesNo}
                                            onChange={(e) => setPutAwayToPickableBinYesNo(e.target.checked)}
                                            disabled={!updateFields.putAwayToPickableBinYesNo}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.placeDirectInPickableBin', {}, 'Legg direkte i plukkbar lokasjon')}</span>
                                    </label>
                                </div>

                                {/* NewLineYesNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.newLineYesNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, newLineYesNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldNewLine', {}, 'Ny linje')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={newLineYesNo}
                                            onChange={(e) => setNewLineYesNo(e.target.checked)}
                                            disabled={!updateFields.newLineYesNo}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.requireNewLineOnPick', {}, 'Krev ny linje ved plukk')}</span>
                                    </label>
                                </div>

                                {/* BarCodeConfirmItem — unntaksflagg vareskann (TW 36344429) */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.barCodeConfirmItem}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, barCodeConfirmItem: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldBarCodeConfirmItem', {}, 'Krev vareskann')}</span>
                                    </label>
                                    <select
                                        value={barCodeConfirmItem}
                                        onChange={(e) => setBarCodeConfirmItem(e.target.value)}
                                        disabled={!updateFields.barCodeConfirmItem}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                        <option value="">{t('manager.modules.adminItemsLocations.itemsBulk.scanFlagInherit', {}, 'Arv (fra profil)')}</option>
                                        <option value="true">{t('manager.modules.adminItemsLocations.itemsBulk.yes', {}, 'Ja')}</option>
                                        <option value="false">{t('manager.modules.adminItemsLocations.itemsBulk.no', {}, 'Nei')}</option>
                                    </select>
                                    <p className="text-xs text-gray-500">{t('manager.modules.adminItemsLocations.itemsBulk.scanFlagHelp', {}, 'Overstyrer profilinnstillingen for disse varene i plukk og vareplassering. Arv = profilen bestemmer.')}</p>
                                </div>
                            </>
                        )}

                        {/* Tab 3: Klassifisering (Classification) */}
                        {activeTab === 'classification' && (
                            <>
                                {/* ItemGrp1 */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.itemGrp1}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, itemGrp1: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldItemGrp1', {}, 'Varegruppe 1')}</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={itemGrp1}
                                        onChange={(e) => setItemGrp1(e.target.value)}
                                        disabled={!updateFields.itemGrp1}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        placeholder={t('manager.modules.adminItemsLocations.itemsBulk.fieldItemGrp1', {}, 'Varegruppe 1')}
                                    />
                                </div>

                                {/* ItemGrp2 */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.itemGrp2}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, itemGrp2: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldItemGrp2', {}, 'Varegruppe 2')}</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={itemGrp2}
                                        onChange={(e) => setItemGrp2(e.target.value)}
                                        disabled={!updateFields.itemGrp2}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        placeholder={t('manager.modules.adminItemsLocations.itemsBulk.fieldItemGrp2', {}, 'Varegruppe 2')}
                                    />
                                </div>

                                {/* ItemGrp3 */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.itemGrp3}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, itemGrp3: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldItemGrp3', {}, 'Varegruppe 3')}</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={itemGrp3}
                                        onChange={(e) => setItemGrp3(e.target.value)}
                                        disabled={!updateFields.itemGrp3}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        placeholder={t('manager.modules.adminItemsLocations.itemsBulk.fieldItemGrp3', {}, 'Varegruppe 3')}
                                    />
                                </div>

                                {/* ItemType - Read-only information */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <p className="text-sm text-blue-800">
                                        <strong>{t('manager.modules.adminItemsLocations.itemsBulk.noteLabel', {}, 'Merk:')}</strong> {t('manager.modules.adminItemsLocations.itemsBulk.itemTypeNote', {}, 'Varetype kan ikke endres ved masseoppdatering. Dette feltet styres av andre systemregler.')}
                                    </p>
                                </div>
                            </>
                        )}

                        {/* Tab 4: Lokasjon (Location) */}
                        {activeTab === 'location' && (
                            <>
                                {/* StdLocationName */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.stdLocationName}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, stdLocationName: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldStdLocation', {}, 'Standard lokasjon')}</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={stdLocationName}
                                        onChange={(e) => setStdLocationName(e.target.value)}
                                        disabled={!updateFields.stdLocationName}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        placeholder={t('manager.modules.adminItemsLocations.itemsBulk.stdLocationPlaceholder', {}, 'Standard lokasjonsnavn')}
                                    />
                                </div>

                                {/* StdBinCode */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.stdBinCode}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, stdBinCode: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldStdBinCode', {}, 'Fast plukklokasjon')}</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={stdBinCode}
                                        onChange={(e) => setStdBinCode(e.target.value)}
                                        disabled={!updateFields.stdBinCode}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        placeholder={t('manager.modules.adminItemsLocations.itemsBulk.stdBinCodePlaceholder', {}, 'Plukklokasjonskode')}
                                    />
                                </div>

                                {/* UseSharedBinYesNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.useSharedBinYesNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, useSharedBinYesNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldSharedBin', {}, 'Delt lokasjon')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={useSharedBinYesNo}
                                            onChange={(e) => setUseSharedBinYesNo(e.target.checked)}
                                            disabled={!updateFields.useSharedBinYesNo}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.allowSharedBin', {}, 'Tillat delt lokasjon')}</span>
                                    </label>
                                </div>

                                {/* UseMaxReplenishQtyYesNo */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.useMaxReplenishQtyYesNo}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, useMaxReplenishQtyYesNo: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldUseMaxReplenish', {}, 'Bruk maks påfyllmengde')}</span>
                                    </label>
                                    <label className="flex items-center space-x-3 px-3 py-2 border border-gray-300 rounded-lg bg-white">
                                        <input
                                            type="checkbox"
                                            checked={useMaxReplenishQtyYesNo}
                                            onChange={(e) => setUseMaxReplenishQtyYesNo(e.target.checked)}
                                            disabled={!updateFields.useMaxReplenishQtyYesNo}
                                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-30"
                                        />
                                        <span className="text-sm text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.limitReplenishQty', {}, 'Begrens påfyllmengde')}</span>
                                    </label>
                                </div>

                                {/* MaxReplenishQty */}
                                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                                    <label className="flex items-center space-x-2">
                                        <input
                                            type="checkbox"
                                            checked={updateFields.maxReplenishQty}
                                            onChange={(e) => setUpdateFields(prev => ({ ...prev, maxReplenishQty: e.target.checked }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.fieldMaxReplenish', {}, 'Maks påfyllmengde')}</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={maxReplenishQty}
                                        onChange={(e) => setMaxReplenishQty(parseFloat(e.target.value) || 0)}
                                        disabled={!updateFields.maxReplenishQty}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        min="0"
                                        step="1"
                                    />
                                </div>
                            </>
                        )}

                        {/* Tab 5: Fritekst (Free Text) */}
                        {activeTab === 'freetext' && (
                            <>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
                                    const fieldName = `freeTxt${num}` as 'freeTxt1' | 'freeTxt2' | 'freeTxt3' | 'freeTxt4' | 'freeTxt5' | 'freeTxt6' | 'freeTxt7' | 'freeTxt8' | 'freeTxt9' | 'freeTxt10';
                                    const getter = {
                                        freeTxt1, freeTxt2, freeTxt3, freeTxt4, freeTxt5,
                                        freeTxt6, freeTxt7, freeTxt8, freeTxt9, freeTxt10
                                    }[fieldName];
                                    const setter = {
                                        freeTxt1: setFreeTxt1, freeTxt2: setFreeTxt2, freeTxt3: setFreeTxt3,
                                        freeTxt4: setFreeTxt4, freeTxt5: setFreeTxt5, freeTxt6: setFreeTxt6,
                                        freeTxt7: setFreeTxt7, freeTxt8: setFreeTxt8, freeTxt9: setFreeTxt9,
                                        freeTxt10: setFreeTxt10
                                    }[fieldName];

                                    return (
                                        <div key={num} className="bg-gray-50 rounded-lg p-4 space-y-3">
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    checked={updateFields[fieldName]}
                                                    onChange={(e) => setUpdateFields(prev => ({ ...prev, [fieldName]: e.target.checked }))}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <span className="text-sm font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': num }, `Fritekst ${num}`)}</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={getter as string}
                                                onChange={(e) => setter(e.target.value)}
                                                disabled={!updateFields[fieldName]}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                                placeholder={t('manager.modules.adminItemsLocations.itemsBulk.freeText', { '0': num }, `Fritekst ${num}`)}
                                            />
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                            {t('manager.modules.adminItemsLocations.itemsBulk.itemsSelected', { '0': selectedItemNos.length }, `${selectedItemNos.length} vare(r) valgt`)}
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={isUpdating || isDeleting}
                                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isDeleting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4" />
                                )}
                                <span>{t('manager.modules.adminItemsLocations.itemsBulk.deleteSelected', {}, 'Slett valgte')}</span>
                            </button>
                            <button
                                onClick={handleUpdate}
                                disabled={!hasSelectedFields() || isUpdating || isDeleting}
                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isUpdating ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4" />
                                )}
                                <span>{t('manager.modules.adminItemsLocations.itemsBulk.updateButton', {}, 'Oppdater')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Update confirmation dialog */}
            {showUpdateConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
                        <div className="p-6 space-y-4">
                            <div className="flex items-start space-x-3">
                                <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900">{t('manager.modules.adminItemsLocations.itemsBulk.confirmUpdateTitle', {}, 'Bekreft oppdatering')}</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {t('manager.modules.adminItemsLocations.itemsBulk.aboutToUpdate', { '0': selectedItemNos.length }, `Du er i ferd med a oppdatere ${selectedItemNos.length} vare(r)`)}
                                    </p>
                                </div>
                            </div>

                            {/* Fields to update */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-blue-900 mb-2">{t('manager.modules.adminItemsLocations.itemsBulk.fieldsToUpdateLabel', {}, 'Felt som vil bli oppdatert:')}</p>
                                <ul className="text-sm text-blue-800 space-y-1 max-h-60 overflow-y-auto">
                                    {getUpdateSummary().map((item, idx) => (
                                        <li key={idx}>• {item}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* ItemNos list */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-900 mb-2">{t('manager.modules.adminItemsLocations.itemsBulk.itemsHeader', {}, 'Varer:')}</p>
                                <div className="max-h-40 overflow-y-auto">
                                    <div className="text-xs text-gray-700 space-y-1">
                                        {selectedItemNos.slice(0, 20).map((itemNo, idx) => (
                                            <div key={idx} className="font-mono">{itemNo}</div>
                                        ))}
                                        {selectedItemNos.length > 20 && (
                                            <div className="text-gray-500 italic">
                                                {t('manager.modules.adminItemsLocations.itemsBulk.andMore', { '0': selectedItemNos.length - 20 }, `... og ${selectedItemNos.length - 20} flere`)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setShowUpdateConfirm(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                onClick={executeUpdate}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                {t('manager.modules.adminItemsLocations.itemsBulk.confirmUpdateTitle', {}, 'Bekreft oppdatering')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirmation dialog */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
                        <div className="p-6 space-y-4">
                            <div className="flex items-start space-x-3">
                                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-gray-900">{t('manager.modules.adminItemsLocations.itemsBulk.confirmDeleteTitle', {}, 'Bekreft sletting')}</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {t('manager.modules.adminItemsLocations.itemsBulk.aboutToDelete', { '0': selectedItemNos.length }, `Du er i ferd med a slette ${selectedItemNos.length} vare(r)`)}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm text-red-800">
                                    <strong>{t('manager.modules.adminItemsLocations.itemsBulk.warningLabel', {}, 'Advarsel:')}</strong> {t('manager.modules.adminItemsLocations.itemsBulk.deleteWarning', {}, 'Denne handlingen kan ikke angres. Varer med saldo eller tilknyttede ordrer vil ikke bli slettet.')}
                                </p>
                            </div>

                            {/* ItemNos list */}
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-900 mb-2">{t('manager.modules.adminItemsLocations.itemsBulk.itemsToDelete', {}, 'Varer som vil slettes:')}</p>
                                <div className="max-h-40 overflow-y-auto">
                                    <div className="text-xs text-gray-700 space-y-1">
                                        {selectedItemNos.slice(0, 20).map((itemNo, idx) => (
                                            <div key={idx} className="font-mono">{itemNo}</div>
                                        ))}
                                        {selectedItemNos.length > 20 && (
                                            <div className="text-gray-500 italic">
                                                {t('manager.modules.adminItemsLocations.itemsBulk.andMore', { '0': selectedItemNos.length - 20 }, `... og ${selectedItemNos.length - 20} flere`)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-4 border-t border-gray-200 bg-gray-50">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                {t('common.buttons.cancel', {}, 'Avbryt')}
                            </button>
                            <button
                                onClick={executeDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                {t('manager.modules.adminItemsLocations.itemsBulk.yesDelete', {}, 'Ja, slett')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
